import React, { Component } from 'react';
import { urlMap, getAuthHeaders } from '../../utils';
import { exportProjectProofOfWorkPdf } from '../../utils/pdfExport';
import { fetchSubscriptionStatus, openSubscriptionTabInWallet } from '../../utils/subscriptionAccess';
import ChapterHistoryDiffModal from '../dashboard/chapterPage/ChapterHistoryDiffModal';

const SNAPSHOTS_PER_PAGE = 20;
const MIN_HEATMAP_WEEKS = 64;

function levelForCount(count) {
  if (count <= 0) return 0;
  if (count === 1) return 1;
  if (count <= 3) return 2;
  if (count <= 6) return 3;
  return 4;
}

function formatLongDuration(secondsInput) {
  const totalMinutes = Math.floor(Math.max(0, Number(secondsInput) || 0) / 60);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0 || days > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  return parts.join(' ');
}

function formatHeatmapDate(dateIso) {
  const date = new Date(`${dateIso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateIso;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Turns a raw git commit message into writer-facing copy — no git terms surfaced in the UI. */
function describeSnapshot(entry) {
  const { type, message } = entry;
  if (type === 'chapter') {
    const rest = String(message || '').replace(/^chapter:\S+\s*—?\s*/, '');
    const label = rest.replace(/\s*—\s*auto snapshot\s*—\s*.+$/i, '').trim();
    return label ? `Chapter edit — ${label}` : 'Chapter edit — autosave';
  }
  if (type === 'reflection') {
    const match = String(message || '').match(/—\s*(\d+)\s*answers/i);
    return match ? `Reflection completed — ${match[1]} answers` : 'Reflection completed';
  }
  if (type === 'init') return 'Project started';
  return message;
}

/**
 * Renders the history tab content — a GitHub-style edit-snapshot heatmap,
 * summary stats, and the full snapshot list — plus a button to export everything as a PDF.
 */
export default class ProjectHistoryView extends Component {
  state = {
    loading: false,
    error: '',
    snapshots: [],
    totalWritingSeconds: 0,
    exporting: false,
    exportError: '',
    snapshotsPage: 1,
    isPro: false,
    proLoading: true,
    diffOpen: false,
    diffCommit: null,
    diffSnapshotContent: null,
    diffCurrentContent: null,
    diffLoading: false,
    diffError: null,
  };

  isUnmounted = false;

  componentDidMount() {
    this.loadSubscription();
    if (this.props.projectId) this.fetchData();
  }

  componentDidUpdate(prevProps) {
    if (this.props.projectId && this.props.projectId !== prevProps.projectId) {
      this.fetchData();
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
  }

  loadSubscription = async () => {
    const status = await fetchSubscriptionStatus();
    if (this.isUnmounted) return;
    this.setState({ isPro: !!status.isPro, proLoading: false });
  };

  fetchData = async () => {
    const { projectId } = this.props;
    if (!projectId) return;

    this.setState({ loading: true, error: '' });
    try {
      const [historyRes, progressFeedRes] = await Promise.all([
        fetch(urlMap.projectHistory(projectId), { headers: getAuthHeaders(), credentials: 'include' }),
        fetch(urlMap.projectProgressFeed(projectId, 1, 1), { headers: getAuthHeaders(), credentials: 'include' }),
      ]);
      if (!historyRes.ok) throw new Error(`HTTP ${historyRes.status}`);
      const body = await historyRes.json();
      const feedBody = progressFeedRes.ok ? await progressFeedRes.json() : null;
      if (this.isUnmounted) return;
      this.setState({
        loading: false,
        snapshots: Array.isArray(body?.commits) ? body.commits : [],
        totalWritingSeconds: Number(feedBody?.totalWritingSeconds) || 0,
        snapshotsPage: 1,
      });
    } catch {
      if (!this.isUnmounted) {
        this.setState({ loading: false, error: 'Could not load your edit history.' });
      }
    }
  };

  handleExport = async () => {
    const { projectId } = this.props;
    if (!projectId) return;
    if (!this.state.isPro) {
      openSubscriptionTabInWallet();
      return;
    }

    this.setState({ exporting: true, exportError: '' });
    try {
      await exportProjectProofOfWorkPdf(projectId);
      if (!this.isUnmounted) this.setState({ exporting: false });
    } catch {
      if (!this.isUnmounted) {
        this.setState({ exporting: false, exportError: 'Could not export PDF.' });
      }
    }
  };

  handleProInfoClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({ exportError: 'Pro subscription required for Proof-of-Work export.' });
  };

  goToPage = (page) => {
    this.setState({ snapshotsPage: page });
  };

  extractChapterIdFromMessage = (message) => {
    const value = String(message || '');
    const match = value.match(/^chapter:([a-zA-Z0-9_-]+)/);
    return match?.[1] || null;
  };

  openDiffForSnapshot = async (snapshot) => {
    const chapterId = this.extractChapterIdFromMessage(snapshot?.message);
    if (!chapterId) return;

    this.setState({
      diffOpen: true,
      diffCommit: snapshot,
      diffSnapshotContent: null,
      diffCurrentContent: null,
      diffLoading: true,
      diffError: null,
    });

    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      const [snapRes, curRes] = await Promise.all([
        fetch(urlMap.getChapterSnapshot(chapterId, snapshot.hash), { headers, credentials: 'include' }),
        fetch(urlMap.getChapter(chapterId), { headers, credentials: 'include' }),
      ]);

      if (!snapRes.ok) throw new Error(`HTTP ${snapRes.status}`);
      if (!curRes.ok) throw new Error(`HTTP ${curRes.status}`);

      const [snapBody, curBody] = await Promise.all([
        snapRes.json().catch(() => ({})),
        curRes.json().catch(() => ({})),
      ]);

      if (this.isUnmounted) return;
      this.setState({
        diffSnapshotContent: snapBody?.content ?? '',
        diffCurrentContent: curBody?.content ?? '',
        diffLoading: false,
      });
    } catch (err) {
      if (this.isUnmounted) return;
      this.setState({
        diffError: err?.message || 'Failed to load diff snapshot',
        diffLoading: false,
      });
    }
  };

  closeDiff = () => {
    this.setState({
      diffOpen: false,
      diffCommit: null,
      diffSnapshotContent: null,
      diffCurrentContent: null,
      diffLoading: false,
      diffError: null,
    });
  };

  buildHeatmapWeeks(snapshots) {
    if (!Array.isArray(snapshots) || snapshots.length === 0) return [];

    const dayCounts = new Map();
    snapshots.forEach((snapshot) => {
      const day = String(snapshot.timestamp).slice(0, 10);
      dayCounts.set(day, (dayCounts.get(day) || 0) + 1);
    });

    const ordered = [...snapshots]
      .map((s) => new Date(s.timestamp))
      .filter((d) => !Number.isNaN(d.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());

    if (ordered.length === 0) return [];

    const firstEdit = new Date(ordered[0]);
    const lastEdit = new Date(ordered[ordered.length - 1]);
    const today = new Date();
    firstEdit.setHours(0, 0, 0, 0);
    lastEdit.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const start = new Date(firstEdit);
    start.setDate(start.getDate() - start.getDay()); // align to Sunday

    const endBase = lastEdit > today ? lastEdit : today;
    const end = new Date(endBase);
    end.setDate(end.getDate() + (6 - end.getDay())); // align to Saturday

    const currentTotalDays = Math.max(7, Math.floor((end.getTime() - start.getTime()) / 86400000) + 1);
    const minDaysToFill = MIN_HEATMAP_WEEKS * 7;
    const totalDays = Math.max(currentTotalDays, minDaysToFill);

    const days = [];
    for (let i = 0; i < totalDays; i += 1) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      const key = date.toISOString().slice(0, 10);
      days.push({ date: key, count: dayCounts.get(key) || 0 });
    }

    const weeks = [];
    for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
    return weeks;
  }

  buildHeatmapMonthLabels(weeks) {
    const labels = [];
    let previousMonth = null;
    weeks.forEach((week, weekIndex) => {
      const firstDay = week[0];
      if (!firstDay?.date) return;
      const date = new Date(`${firstDay.date}T00:00:00`);
      const month = date.getMonth();
      if (month !== previousMonth) {
        labels.push({
          key: `${date.getFullYear()}-${month}`,
          weekIndex,
          label: date.toLocaleDateString(undefined, { month: 'short', year: '2-digit' }),
        });
        previousMonth = month;
      }
    });
    return labels;
  }

  render() {
    const {
      loading,
      error,
      snapshots,
      totalWritingSeconds,
      exporting,
      exportError,
      snapshotsPage,
      isPro,
      proLoading,
      diffOpen,
      diffCommit,
      diffSnapshotContent,
      diffCurrentContent,
      diffLoading,
      diffError,
    } = this.state;

    if (loading) {
      return (
        <div className="public-progress-view public-progress-view--loading">
          <p>Loading history…</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="public-progress-view public-progress-view--error">
          <p>{error}</p>
        </div>
      );
    }

    const weeks = this.buildHeatmapWeeks(snapshots);
    const chapterCount = snapshots.filter((s) => s.type === 'chapter').length;
    const monthLabels = this.buildHeatmapMonthLabels(weeks);
    const totalPages = Math.max(1, Math.ceil(snapshots.length / SNAPSHOTS_PER_PAGE));
    const currentPage = Math.min(snapshotsPage, totalPages);
    const pagedSnapshots = snapshots.slice(
      (currentPage - 1) * SNAPSHOTS_PER_PAGE,
      currentPage * SNAPSHOTS_PER_PAGE,
    );

    return (
      <div className="project-history-view">        
        <div className="project-history-view__toolbar">
          <dl className="project-history-view__stats">
            <div>
              <dt>Total writing time</dt>
              <dd>{formatLongDuration(totalWritingSeconds)}</dd>
            </div>
            <div>
              <dt>Edit snapshots</dt>
              <dd>{snapshots.length}</dd>
            </div>
            <div>
              <dt>Chapter snapshots</dt>
              <dd>{chapterCount}</dd>
            </div>
          </dl>
          <div className="project-history-view__export-actions">
            <button
              type="button"
              className="project-history-view__export-btn"
              onClick={this.handleExport}
              disabled={exporting || proLoading}
              title="Export history (PDF)"
            >
              {!isPro && !exporting && (
                <span className="project-history-view__export-lock" aria-hidden="true">
                  <svg viewBox="0 0 20 20" width="12" height="12" fill="currentColor">
                    <path fillRule="evenodd" d="M10 1a4 4 0 0 0-4 4v2H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-1V5a4 4 0 0 0-4-4Zm2 6V5a2 2 0 1 0-4 0v2h4Z" clipRule="evenodd" />
                  </svg>
                </span>
              )}
              <span>{exporting ? 'Exporting…' : 'Export history'}</span>
            </button>
            {!isPro && !exporting && (
              <button
                type="button"
                className="project-history-view__export-info"
                onClick={this.handleProInfoClick}
                title="Why Pro is required"
                aria-label="Why Pro is required"
              >
                i
              </button>
            )}
          </div>
        </div>

        {exportError && <div className="project-history-view__error">{exportError}</div>}

        <div className="project-history-view__heatmap-scroll">
          <div
            className="project-history-view__heatmap-months"
            style={{ gridTemplateColumns: `repeat(${weeks.length}, 10px)` }}
            aria-hidden="true"
          >
            {monthLabels.map((monthLabel) => (
              <span
                key={monthLabel.key}
                className="project-history-view__heatmap-month"
                style={{ gridColumnStart: monthLabel.weekIndex + 1 }}
              >
                {monthLabel.label}
              </span>
            ))}
          </div>

          <div className="project-history-view__heatmap" role="img" aria-label="Writing activity heatmap">
            {weeks.map((week, weekIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className="project-history-view__heatmap-col" key={weekIndex}>
                {week.map((day) => (
                  <div
                    key={day.date}
                    className={`project-history-view__heatmap-cell project-history-view__heatmap-cell--level-${levelForCount(day.count)}`}
                    title={`${formatHeatmapDate(day.date)}: ${day.count} edit snapshot${day.count === 1 ? '' : 's'}`}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>

        <ul className="project-history-view__commit-list">
          {snapshots.length === 0 && (
            <li className="project-history-view__empty">No edit snapshots yet — start writing to build your history.</li>
          )}
          {pagedSnapshots.map((snapshot) => (
            <li key={snapshot.hash} className={`project-history-view__commit project-history-view__commit--${snapshot.type}`}>
              {snapshot.type === 'chapter' ? (
                <button
                  type="button"
                  className="project-history-view__commit-btn"
                  onClick={() => this.openDiffForSnapshot(snapshot)}
                  title="View side-by-side diff"
                  aria-label="View side-by-side diff"
                >
                  <span className="project-history-view__commit-time">
                    {new Date(snapshot.timestamp).toLocaleString()}
                  </span>
                  <span className="project-history-view__commit-message">{describeSnapshot(snapshot)}</span>
                </button>
              ) : (
                <>
                  <span className="project-history-view__commit-time">
                    {new Date(snapshot.timestamp).toLocaleString()}
                  </span>
                  <span className="project-history-view__commit-message">{describeSnapshot(snapshot)}</span>
                </>
              )}
            </li>
          ))}
        </ul>

        {snapshots.length > 0 && (
          <div className="project-history-view__pagination">
            <button
              type="button"
              onClick={() => this.goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              Previous
            </button>
            <span className="project-history-view__pagination-label">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => this.goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              Next
            </button>
          </div>
        )}

        <ChapterHistoryDiffModal
          isOpen={diffOpen}
          onClose={this.closeDiff}
          currentContent={diffLoading ? null : (diffCurrentContent ?? '')}
          snapshotContent={diffLoading ? null : (diffSnapshotContent ?? '')}
          commitInfo={diffCommit}
          snapshotLoading={diffLoading}
          snapshotError={diffError}
        />
      </div>
    );
  }
}
